import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
} from '@hello-pangea/dnd';
import { Close } from '@mui/icons-material';
import {
  Divider,
  Dropdown,
  IconButton,
  ListItemDecorator,
  Menu,
  MenuButton,
  MenuItem,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/joy';
import {
  CalendarBlank,
  CaretDown,
  DotsSix,
  Equals,
  Plus,
  PlusCircle,
  TextT,
  Trash,
  WarningCircle,
} from '@phosphor-icons/react';
import clsx from 'clsx';
import { ReactNode, useState } from 'react';
import {
  FormProvider,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';

import LongResponseIcon from '~/assets/svg/final-report/template-builder/long-response.svg?react';
import ShortResponseIcon from '~/assets/svg/final-report/template-builder/short-response.svg?react';
import DropdownIcon from '~/assets/svg/finalReport/insertMenu/dropdownIcon.svg?react';
import FixedFields from '~/components/customer/settings/finalReport/templateDialogs/common/fixedFields';
import DeleteTemplateDialog from '~/components/customer/settings/finalReport/templateDialogs/deleteTemplateDialog';
import { Button } from '~/components/ui/button';
import { Card, CardOverflow } from '~/components/ui/card';
import { CheckboxField } from '~/components/ui/checkbox';
import { Dialog, DialogContent, DialogTrigger } from '~/components/ui/dialog';
import { Label } from '~/components/ui/fieldset';
import { Field, useFormController } from '~/components/ui/form';
import { Input, InputField } from '~/components/ui/input';
import {
  EditorControls,
  FontSizeMenu,
  HeadingMenu,
  RichTextEditor,
  RichTextEditorContent,
} from '~/components/ui/rich-text-editor';
import { Option, SelectField } from '~/components/ui/select';
import { Switch, SwitchField } from '~/components/ui/switch';
import { Text } from '~/components/ui/text';
import { TextAreaField } from '~/components/ui/textarea';
import { useTenantSlug } from '~/features/tenant/helpers';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { getCurrentIntl } from '~/i18n/intl/intl';
import { DynamicFieldResponse } from '~/interfaces/DynamicFieldResponse';
import { FinalReportTemplate } from '~/interfaces/FinalReportTemplate';
import { FinalReportService } from '~/services/api/final-report/endpoints';
import { useWrappedMutation } from '~/services/api/rest-api';
import { RequestErrorBanner } from '~/services/api/rest-api-error';
import { useForm } from '~/utils/forms';

enum FieldTypes {
  ShortResponse = 0,
  LongResponse,
  Title,
  Description,
  Date,
  DropdownField,
}

const baseDynamicFieldSchema = z.object({
  id: z.number().optional(),
  type: z.number(),
  value: z.string(),
  fieldTitle: z.string().min(1, { message: 'Field title is required. ' }),
  placeholder: z.string(),
  isMandatory: z.boolean(),
  dropdownItems: z
    .array(
      z.object({
        value: z
          .string()
          .min(1, { message: 'Dropdown item must not be empty. ' }),
      }),
    )
    .max(10),
});

export type DynamicField = z.infer<typeof baseDynamicFieldSchema> & {
  siblings?: DynamicField[];
};

const siblingDynamicFieldSchema: z.ZodType<DynamicField> =
  baseDynamicFieldSchema.extend({
    siblings: z.lazy(() => baseDynamicFieldSchema.array()),
  });

export type DynamicFieldWithSibling = z.infer<typeof siblingDynamicFieldSchema>;

const templateSchema = z.object({
  id: z.number().optional(),
  isPopulated: z.boolean(),
  templateTitle: z.string().min(1, { message: 'Template title is required. ' }),
  templateFields: z.array(siblingDynamicFieldSchema).min(1),
  populatePermissions: z.object({
    caseManager: z.boolean(),
    dateReceived: z.boolean(),
    reportType: z.boolean(),
    reportReference: z.boolean(),
    reportSeverity: z.boolean(),
  }),
});

const buttonSchema = z.object({
  variant: z.enum(['soft', 'plain']),
  handleClick: z.function().args(z.nativeEnum(FieldTypes)).returns(z.void()),
  shape: z.enum(['pill', 'default']).optional(),
});

type ButtonProps = z.infer<typeof buttonSchema>;

export type BuilderFields = z.infer<typeof templateSchema>;

const getTypeText = (type: FieldTypes) => {
  const intl = getCurrentIntl();
  switch (type) {
    case FieldTypes.ShortResponse:
      return intl.formatMessage({
        defaultMessage: 'Short Response',
      });
    case FieldTypes.LongResponse:
      return intl.formatMessage({
        defaultMessage: 'Long Response',
      });
    case FieldTypes.Date:
      return intl.formatMessage({
        defaultMessage: 'Date',
      });
    case FieldTypes.Description:
      return intl.formatMessage({
        defaultMessage: 'Description',
      });
    case FieldTypes.DropdownField:
      return intl.formatMessage({
        defaultMessage: 'Dropdown',
      });
    case FieldTypes.Title:
      return intl.formatMessage({
        defaultMessage: 'Title',
      });
    default:
      return '';
  }
};

const TypeIcon = ({ type, color }: { type: FieldTypes; color?: string }) => {
  switch (type) {
    case FieldTypes.ShortResponse:
      return <ShortResponseIcon fill={color} />;
    case FieldTypes.LongResponse:
      return <LongResponseIcon fill={color} />;
    case FieldTypes.Date:
      return <CalendarBlank color={color} />;
    case FieldTypes.Description:
      return <TextT color={color} />;
    case FieldTypes.DropdownField:
      return <DropdownIcon fill={color} />;
    case FieldTypes.Title:
      return <Equals color={color} />;
    default:
      return <></>;
  }
};

const AddTitleButton = (props: ButtonProps) => {
  return (
    <Button
      color='neutral'
      startDecorator={<Equals />}
      onClick={() => props.handleClick(FieldTypes.Title)}
      variant={props.variant}
      shape={props.shape}
      type='button'
    >
      <FormattedMessage defaultMessage='Add title' />
    </Button>
  );
};

const AddDescriptionButton = (props: ButtonProps) => {
  return (
    <Button
      color='neutral'
      startDecorator={<TextT />}
      onClick={() => props.handleClick(FieldTypes.Description)}
      variant={props.variant}
      shape={props.shape}
      type='button'
    >
      <FormattedMessage defaultMessage='Add description' />
    </Button>
  );
};

const AddQuestionMenu = (props: ButtonProps) => {
  return (
    <Dropdown>
      <MenuButton
        type='button'
        variant={props.variant}
        startDecorator={<PlusCircle />}
        endDecorator={<CaretDown />}
        sx={{
          borderRadius: props.shape === 'pill' ? 999 : 0,
        }}
      >
        <FormattedMessage defaultMessage='Add field' />
      </MenuButton>
      <Menu disablePortal>
        <MenuItem onClick={() => props.handleClick(FieldTypes.ShortResponse)}>
          <ListItemDecorator>
            <ShortResponseIcon />
          </ListItemDecorator>{' '}
          {getTypeText(FieldTypes.ShortResponse)}
        </MenuItem>
        <MenuItem onClick={() => props.handleClick(FieldTypes.LongResponse)}>
          <ListItemDecorator>
            <LongResponseIcon />
          </ListItemDecorator>{' '}
          {getTypeText(FieldTypes.LongResponse)}
        </MenuItem>
        <MenuItem onClick={() => props.handleClick(FieldTypes.Date)}>
          <ListItemDecorator>
            <CalendarBlank />
          </ListItemDecorator>{' '}
          {getTypeText(FieldTypes.Date)}
        </MenuItem>
        <MenuItem onClick={() => props.handleClick(FieldTypes.DropdownField)}>
          <ListItemDecorator>
            <DropdownIcon />
          </ListItemDecorator>{' '}
          {getTypeText(FieldTypes.DropdownField)}
        </MenuItem>
      </Menu>
    </Dropdown>
  );
};

function DescriptionField() {
  const controller = useFormController();
  const isInvalid = !!controller?.fieldState.invalid;

  return (
    <RichTextEditor>
      <Card
        size='sm'
        color={isInvalid ? 'danger' : 'neutral'}
        variant='outlined'
      >
        <RichTextEditorContent />
        <Divider />
        <div className='flex items-center w-full gap-2'>
          <EditorControls.Formatting />
          <Divider orientation='vertical' />
          <HeadingMenu />
          <FontSizeMenu />
          <Divider orientation='vertical' />
          <ToggleButtonGroup spacing={0.5} variant='plain' size='sm'>
            <EditorControls.Bold />
            <EditorControls.Underline />
            <EditorControls.Italic />
          </ToggleButtonGroup>
          <Divider orientation='vertical' />
          <ToggleButtonGroup spacing={0.5} variant='plain' size='sm'>
            <EditorControls.OrderedList />
            <EditorControls.UnorderedList />
          </ToggleButtonGroup>
        </div>
      </Card>
    </RichTextEditor>
  );
}

function DraggableFieldCard({
  provider,
  snapshot,
  children,
}: {
  provider: DraggableProvided;
  snapshot: DraggableStateSnapshot;
  children: ReactNode;
}) {
  return (
    <Card
      ref={provider.innerRef}
      {...provider.draggableProps}
      {...provider.dragHandleProps}
      className={clsx(
        snapshot.isDragging
          ? 'ring-1 ring-offset-2 ring-brand-primary rounded'
          : '',
      )}
    >
      <CardOverflow bleed>
        <div className='flex items-center justify-center bg-brand-primary rounded-t h-2' />
        <div className='flex justify-center items-center px-2 bg-neutral-50 hover:bg-neutral-200 active:bg-neutral-300'>
          <DotsSix />
        </div>
      </CardOverflow>
      {children}
    </Card>
  );
}

function FieldCardBody({ index }: { index: number }) {
  const form = useFormContext();
  const intl = useIntl();
  const field = useWatch({
    control: form.control,
    name: `templateFields.${index}`,
  });
  const { fields, append, remove } = useFieldArray({
    name: `templateFields.${index}.dropdownItems`,
  });

  const getPlaceHolder = () => {
    switch (field.type) {
      case FieldTypes.Title:
        return intl.formatMessage({ defaultMessage: 'Add title here' });
      case FieldTypes.Description:
        return intl.formatMessage({ defaultMessage: 'Add description here' });
      default:
        return intl.formatMessage({ defaultMessage: 'Add field' });
    }
  };

  return (
    <>
      {field.type === FieldTypes.Description && (
        <Field
          size='lg'
          control={form.control}
          field={`templateFields.${index}.fieldTitle`}
        >
          <DescriptionField />
        </Field>
      )}
      {field.type !== FieldTypes.Description && (
        <InputField
          variant='soft'
          control={form.control}
          field={`templateFields.${index}.fieldTitle`}
          placeholder={getPlaceHolder()}
          size='lg'
        />
      )}
      {field.type === FieldTypes.ShortResponse && (
        <InputField
          variant='soft'
          control={form.control}
          field={`templateFields.${index}.placeholder`}
          placeholder={intl.formatMessage({
            defaultMessage: 'Short response text',
          })}
          size='lg'
          className='w-1/2'
        />
      )}
      {field.type === FieldTypes.LongResponse && (
        <TextAreaField
          variant='soft'
          control={form.control}
          field={`templateFields.${index}.placeholder`}
          placeholder={intl.formatMessage({
            defaultMessage: 'Long response text',
          })}
          size='lg'
          minRows={4}
        />
      )}
      {field.type === FieldTypes.DropdownField && (
        <>
          {fields.map((dropdownItem, itemIndex) => (
            <div className='flex gap-4' key={dropdownItem.id}>
              <div className='flex-1'>
                <InputField
                  control={form.control}
                  field={`templateFields.${index}.dropdownItems.${itemIndex}.value`}
                  variant='soft'
                  placeholder={`${intl.formatMessage({
                    defaultMessage: 'Option',
                  })} ${intl.formatNumber(itemIndex + 1)}`}
                />
              </div>
              {itemIndex > 0 && (
                <IconButton color='danger' onClick={() => remove(itemIndex)}>
                  <Close />
                </IconButton>
              )}
            </div>
          ))}
          {fields.length < 10 && (
            <Button variant='plain' onClick={() => append({ value: '' })}>
              <FormattedMessage defaultMessage='Add another option' />
            </Button>
          )}
        </>
      )}
      {field.type === FieldTypes.Date && (
        <Input
          variant='soft'
          placeholder={intl.formatMessage({
            defaultMessage: 'Select date',
          })}
          size='lg'
          disabled
          className='w-1/2'
          startDecorator={<CalendarBlank />}
        />
      )}
    </>
  );
}

function FieldCardFooter({
  index,
  onRemove,
}: {
  index: number;
  onRemove: (index: number) => void;
}) {
  const form = useFormContext();
  const field = useWatch({
    control: form.control,
    name: `templateFields.${index}`,
  });

  return (
    <div className='border-t border-t-gray-200 flex justify-between items-center gap-4 pt-4'>
      <SelectField
        indicator={<CaretDown />}
        control={form.control}
        field={`templateFields.${index}.type`}
        startDecorator={<TypeIcon type={field.type} color='teal' />}
        variant='soft'
        slotProps={{
          listbox: {
            disablePortal: true,
          },
          root: {
            sx: {
              borderRadius: 999,
            },
          },
        }}
        onChange={(e, value) => {
          e?.preventDefault();
          form.setValue(`templateFields.${index}.placeholder`, '');
          form.setValue(`templateFields.${index}.fieldTitle`, '');
          if (value === FieldTypes.DropdownField) {
            form.setValue(`templateFields.${index}.dropdownItems`, [
              { value: '' },
            ]);
          } else {
            form.setValue(`templateFields.${index}.dropdownItems`, []);
          }
        }}
        color='primary'
      >
        <Option key={FieldTypes.ShortResponse} value={FieldTypes.ShortResponse}>
          {getTypeText(FieldTypes.ShortResponse)}
        </Option>
        <Option key={FieldTypes.LongResponse} value={FieldTypes.LongResponse}>
          {getTypeText(FieldTypes.LongResponse)}
        </Option>
        <Option key={FieldTypes.Title} value={FieldTypes.Title}>
          {getTypeText(FieldTypes.Title)}
        </Option>
        <Option key={FieldTypes.Description} value={FieldTypes.Description}>
          {getTypeText(FieldTypes.Description)}
        </Option>
        <Option key={FieldTypes.DropdownField} value={FieldTypes.DropdownField}>
          {getTypeText(FieldTypes.DropdownField)}
        </Option>
        <Option key={FieldTypes.Date} value={FieldTypes.Date}>
          {getTypeText(FieldTypes.Date)}
        </Option>
      </SelectField>
      <div className='flex items-center justify-center gap-4'>
        <IconButton type='button' onClick={() => onRemove(index)}>
          <Trash />
        </IconButton>
        {field.type !== FieldTypes.Description &&
          field.type !== FieldTypes.Title && (
            <>
              {' '}
              <Divider orientation='vertical' />
              <div className='flex gap-3'>
                <Label>
                  <FormattedMessage defaultMessage='Required' />
                </Label>
                <SwitchField
                  size='lg'
                  control={form.control}
                  field={`templateFields.${index}.isMandatory`}
                />
              </div>
            </>
          )}
      </div>
    </div>
  );
}

export function FinalReportTemplateBuilder({
  defaultValues,
}: {
  defaultValues?: Partial<BuilderFields>;
}) {
  const [open, setOpen] = useState(false);
  const slug = useTenantSlug();
  const { createToastNotification } = useToastNotificationContext();
  const form = useForm({
    defaultValues: {
      id: defaultValues?.id || -1,
      isPopulated: defaultValues?.isPopulated || false,
      populatePermissions: {
        caseManager: defaultValues?.populatePermissions?.caseManager || false,
        dateReceived: defaultValues?.populatePermissions?.caseManager || false,
        reportType: defaultValues?.populatePermissions?.reportType || false,
        reportReference:
          defaultValues?.populatePermissions?.reportReference || false,
        reportSeverity:
          defaultValues?.populatePermissions?.reportSeverity || false,
      },
      templateTitle: defaultValues?.templateTitle || '',
      templateFields: defaultValues?.templateFields || [],
    },
    schema: templateSchema,
  });
  const fieldArray = useFieldArray({
    control: form.control,
    name: 'templateFields',
  });
  const createTemplate = useWrappedMutation(
    FinalReportService.endpoints.createTemplate.useMutation(),
  );
  const editTemplate = useWrappedMutation(
    FinalReportService.endpoints.editTemplate.useMutation(),
  );
  const intl = useIntl();

  const permissionsEnabled = form.watch('isPopulated');
  const permissions = form.watch('populatePermissions');
  const title = form.watch('templateTitle');
  const showFixedField = Object.values(permissions).some(
    (value) => value === true,
  );
  const tooltipMessage = intl.formatMessage({
    defaultMessage:
      'Enabling this will automatically\npopulate fields in the template',
  });

  const appendField = (type: FieldTypes) => {
    const items = [];
    if (type === FieldTypes.DropdownField) {
      items.push({ value: '' });
    }
    fieldArray.append({
      type: type,
      value: '',
      fieldTitle: '',
      placeholder: '',
      isMandatory: false,
      dropdownItems: items,
      siblings: [],
    });
  };

  const submitHandler = form.handleSubmit(async (data) => {
    const customFields: DynamicFieldResponse[] = data.templateFields.map(
      (field) => {
        return {
          label: field.fieldTitle,
          dropdown_choices: field.dropdownItems.map((item) => item.value),
          is_mandatory: field.isMandatory,
          type: field.type,
          placeholder: field.placeholder,
          siblings: [],
        };
      },
    );

    const finalReportTemplate: FinalReportTemplate = {
      template_name: data.templateTitle,
      template_title: data.templateTitle,
      case_manager_name: data.populatePermissions.caseManager,
      date_received: data.populatePermissions.dateReceived,
      report_type: data.populatePermissions.reportType,
      report_reference: data.populatePermissions.reportReference,
      report_severity: data.populatePermissions.reportSeverity,
      final_report_custom_field: customFields,
    };

    if (defaultValues?.id) {
      await editTemplate.mutateAsync(
        {
          slug: slug,
          template: finalReportTemplate,
          id: defaultValues.id,
        },
        {
          onSuccess: () => {
            form.reset();
            createToastNotification(
              intl.formatMessage({
                defaultMessage: 'Template successfully edited.',
              }),
              'success',
            );
            setOpen(false);
          },
        },
      );
      return;
    }

    await createTemplate.mutateAsync(
      {
        slug: slug,
        template: finalReportTemplate,
      },
      {
        onSuccess: () => {
          form.reset();
          createToastNotification(
            intl.formatMessage({ defaultMessage: 'Template added.' }),
            'success',
          );
          setOpen(false);
        },
      },
    );
  });

  const resetOnClose = (value: boolean) => {
    if (!value) {
      form.reset();
    }
    setOpen(value);
  };

  const discardChanges = () => {
    form.reset();
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={resetOnClose}>
      <DialogTrigger asChild>
        {defaultValues ? (
          <Button color='secondary' variant='plain' size='md'>
            <FormattedMessage defaultMessage='Edit' />
          </Button>
        ) : (
          <Button
            color='neutral'
            variant='outlined'
            size='md'
            startDecorator={<Plus />}
          >
            <FormattedMessage defaultMessage='Template' />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent size='5xl' asChild>
        <Card>
          <RequestErrorBanner
            error={createTemplate.state.error || editTemplate.state.error}
          />
          <FormProvider {...form}>
            <form onSubmit={submitHandler} className='space-y-4'>
              <div className='flex justify-between items-center'>
                <Text level='title-lg'>
                  {title ||
                    intl.formatMessage({ defaultMessage: 'Unnamed Template' })}
                </Text>
                <div className='flex gap-4'>
                  {defaultValues && (
                    <DeleteTemplateDialog
                      templateID={defaultValues.id}
                      templateName={defaultValues.templateTitle || 'Template'}
                      inEdit
                    />
                  )}
                  <Button
                    variant='plain'
                    color='neutral'
                    type='button'
                    onClick={discardChanges}
                    disabled={form.formState.isSubmitting}
                  >
                    <FormattedMessage defaultMessage='Discard changes' />
                  </Button>
                  <Button
                    color='secondary'
                    type='submit'
                    disabled={form.formState.isSubmitting}
                  >
                    {defaultValues ? (
                      <FormattedMessage defaultMessage='Save edit' />
                    ) : (
                      <FormattedMessage defaultMessage='Add template' />
                    )}
                  </Button>
                </div>
              </div>
              <CardOverflow className='gap-4'>
                <Divider inset='context' />
                <div className='flex gap-4 items-center'>
                  <Text level='body-lg' className='font-medium'>
                    <FormattedMessage defaultMessage='Fields Pre-population' />
                  </Text>
                  <div className='flex items-center gap-3'>
                    <Switch
                      controlled={false}
                      defaultChecked={defaultValues?.isPopulated}
                      onChange={(e) => {
                        if (!e.target.checked) {
                          form.setValue('populatePermissions', {
                            caseManager: false,
                            dateReceived: false,
                            reportType: false,
                            reportReference: false,
                            reportSeverity: false,
                          });
                        }
                        form.setValue('isPopulated', e.target.checked);
                      }}
                    />
                    <Tooltip
                      title={
                        <span style={{ whiteSpace: 'pre-line' }}>
                          {tooltipMessage}
                        </span>
                      }
                      variant='solid'
                      arrow
                      placement='top'
                    >
                      <WarningCircle weight='fill' size={20} />
                    </Tooltip>
                  </div>
                </div>
                {permissionsEnabled && (
                  <div className='flex flex-row gap-4 items-center'>
                    <CheckboxField
                      control={form.control}
                      field='populatePermissions.caseManager'
                      label={intl.formatMessage({
                        defaultMessage: 'Case Manager Name',
                      })}
                    />
                    <CheckboxField
                      control={form.control}
                      field='populatePermissions.dateReceived'
                      label={intl.formatMessage({
                        defaultMessage: 'Date Received',
                      })}
                    />
                    <CheckboxField
                      control={form.control}
                      field='populatePermissions.reportType'
                      label={intl.formatMessage({
                        defaultMessage: 'Report Type',
                      })}
                    />
                    <CheckboxField
                      control={form.control}
                      field='populatePermissions.reportReference'
                      label={intl.formatMessage({
                        defaultMessage: 'Report Reference',
                      })}
                    />
                    <CheckboxField
                      control={form.control}
                      field='populatePermissions.reportSeverity'
                      label={intl.formatMessage({
                        defaultMessage: 'Report Severity',
                      })}
                    />
                  </div>
                )}
                <Divider inset='context' />
                <div className='flex gap-2 items-center'>
                  <AddTitleButton variant='plain' handleClick={appendField} />
                  <AddDescriptionButton
                    variant='plain'
                    handleClick={appendField}
                  />
                  <AddQuestionMenu variant='plain' handleClick={appendField} />
                </div>
              </CardOverflow>

              <div className='max-w-4xl w-full flex flex-col flex-1 m-auto gap-5'>
                {showFixedField && <FixedFields permissions={permissions} />}
                <InputField
                  control={form.control}
                  field='templateTitle'
                  variant='plain'
                  placeholder={intl.formatMessage({
                    defaultMessage: 'Give this template a title',
                  })}
                  size='lg'
                  sx={{
                    fontSize: 18,
                  }}
                />
                <DragDropContext
                  onDragEnd={(result) => {
                    if (!result.destination) {
                      return;
                    }
                    if (result.destination.index === result.source.index) {
                      return;
                    }
                    fieldArray.move(
                      result.source.index,
                      result.destination.index,
                    );
                  }}
                >
                  <Droppable droppableId={'templateFields'}>
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className='p-3 flex flex-col gap-4'
                      >
                        {fieldArray.fields.map((field, index) => (
                          <Draggable
                            key={field.id}
                            index={index}
                            draggableId={`template-field-${field.id}`}
                            disableInteractiveElementBlocking={false}
                          >
                            {(providedItem, snapshot) => (
                              <DraggableFieldCard
                                provider={providedItem}
                                snapshot={snapshot}
                              >
                                <RichTextEditor>
                                  <FieldCardBody index={index} />
                                  <FieldCardFooter
                                    index={index}
                                    onRemove={fieldArray.remove}
                                  />
                                </RichTextEditor>
                              </DraggableFieldCard>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
                <div className='flex flex-row gap-4 justify-center items-center'>
                  <AddTitleButton
                    variant='soft'
                    shape='pill'
                    handleClick={appendField}
                  />
                  <AddDescriptionButton
                    variant='soft'
                    shape='pill'
                    handleClick={appendField}
                  />
                  <AddQuestionMenu
                    variant='soft'
                    shape='pill'
                    handleClick={appendField}
                  />
                </div>
              </div>
            </form>
          </FormProvider>
        </Card>
      </DialogContent>
    </Dialog>
  );
}
